import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import cookies from 'js-cookie';
import { notifyErrorDebounce } from '@/components';
import { message } from 'antd';

export const REQUEST_ERROR_STATUS = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  405: '请求方法不被允许。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

const intl = (val: string) => val;

// 这些错误码由业务侧自行处理（如触发图形验证码弹窗），不弹全局错误提示
const SILENT_ERROR_CODES = ['UNLOCK_CAPTCHA_REQUIRED'];

const renderStatusMessage = (status: number, intl: (val: string) => string) => {
  return intl(REQUEST_ERROR_STATUS[(status ?? 0) as keyof {}]);
};

const globalErrorHandler = (error: any) => {
  if (error.response) {
    const response = error.response;
    const { status } = response;
    if (status === 401) {
      notifyErrorDebounce({
        description: intl(`用户没有权限（令牌、用户名、密码错误）。`),
        message: intl('异常提示'),
      });
      return;
    }
    notifyErrorDebounce({
      description:
        response?.data?.message ||
        renderStatusMessage(response?.status ?? 0, intl),
      message: intl('异常提示'),
    });

    throw error;
  }
};

class RequestError extends Error {
  errorCode?: string;
  
  constructor(message: string, errorCode?: string) {
    super(message);
    this.errorCode = errorCode;
  }
}

type CreateRequestOptions = {
  errorHandler: (error: any) => void;
};

export type ResponseData<D = unknown> = {
  data: D;
  err?: {
    msg?: string;
  };
  errorDetail?: string;
  errorMsg?: string;
  errorMessage?: string;
  errorCode?: string;
  message?: string;
  success: boolean;
};
//  AxiosResponse<ResponseData>
export const createRequest = (
  config: AxiosRequestConfig = {},
  options: Partial<CreateRequestOptions> = {}
) => {
  const { errorHandler = globalErrorHandler } = options;
  const request = axios.create({ ...config });

  request.interceptors.request.use(
    (requestConfig: AxiosRequestConfig): any => {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          // @ts-ignore
          'Accept-Language': 'zh_CN' || cookies.get('trantor_v2_lng'),
        },
      };
    },

    (error: any) => errorHandler(error)
  );
  request.interceptors.response.use(
    (response: AxiosResponse<ResponseData>): any => {
      const { data } = response;
      if (response.config.url && /\.json$/.test(response.config.url)) {
        if(data.success !== undefined && data.success === false &&data?.errorMsg){
          message.error(data?.errorMsg)
        }
        return data;
      }

      if ((data.success !== undefined && data.success === false) || (data.message && data.message !== "success")) {
        const errMsg = data?.errorDetail || data?.errorMsg || data?.errorMessage||data?.err?.msg;
        const errCode = data?.errorCode;
        // 不提示用户登录
        const isLoginMsg = '用户登录失效，请重新登录';
        // 要修改的错误提示
        if (
          errMsg &&
          !isLoginMsg.includes(errMsg) &&
          !SILENT_ERROR_CODES.includes(errCode ?? '')
        ) {
          const errMessage = errMsg === "订阅企业规则入参为空" ? "抱歉，当前企业不支持订阅" : errMsg;
          notifyErrorDebounce({
            description: errMessage as string,
            message: intl('异常提示'),
          });
        }
        
        throw new RequestError(errMsg || '请求失败', errCode);
      }

      // 对mainSearchV2 接口进行特殊处理
      if(response.config.url && (response.config.url.includes("/bcpcrm/rest/api/opportunity/v2/mainSearchV2") || response.config.url.includes("/bcpcrm/rest/api/opportunity/v2/aiCustomizedSearchV2"))){
        return data
      }
      return data.data;
    },
    (error: any) => errorHandler(error)
  );
  return request;
};

export const request = createRequest();
